// frontend/src/pages/admin/CVGeneration/CVPreviewPanel.js
import React from 'react';
import { FileText, Download, RefreshCw, AlertCircle, CheckCircle, User, Eye, FileDown } from 'lucide-react';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';

/**
 * Quality Score Badge - shows overall data completeness.
 */
const QualityScoreBadge = ({ score }) => {
  let bgColor, textColor, label;
  
  if (score >= 80) {
    bgColor = 'bg-green-100';
    textColor = 'text-green-700';
    label = 'Complete';
  } else if (score >= 50) {
    bgColor = 'bg-amber-100';
    textColor = 'text-amber-700';
    label = 'Partial';
  } else {
    bgColor = 'bg-red-100';
    textColor = 'text-red-700';
    label = 'Incomplete';
  }

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${bgColor}`}>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full ${
              score >= i * 20 ? (score >= 80 ? 'bg-green-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500') : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
      <span className={`text-sm font-medium ${textColor}`}>
        {Math.round(score)}% {label}
      </span>
    </div>
  );
};

/**
 * Missing Fields Alert - shows what data is missing.
 */
const MissingFieldsAlert = ({ missingFields = [] }) => {
  if (missingFields.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-amber-800">Missing Information</p>
          <ul className="mt-1 text-sm text-amber-700 list-disc list-inside">
            {missingFields.slice(0, 5).map((field, idx) => (
              <li key={idx}>{field}</li>
            ))}
            {missingFields.length > 5 && (
              <li className="text-amber-600">+{missingFields.length - 5} more...</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

/**
 * Empty State - shown when no employee is selected.
 */
const EmptyState = ({ selectedCount }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-8">
    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Eye className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">
      Select an Employee to Preview
    </h3>
    <p className="text-gray-500 max-w-md">
      Click on an employee from the list to preview their CV data.
      {selectedCount > 0 && (
        <span className="block mt-2 text-red-600 font-medium">
          {selectedCount} employee{selectedCount !== 1 ? 's' : ''} selected for batch generation.
        </span>
      )}
    </p>
  </div>
);

/**
 * CV Preview Panel for CV Generation.
 * 
 * Features:
 * - HTML preview of CV
 * - Data quality score
 * - Missing fields warning
 * - Download PDF button
 * - Regenerate option
 */
const CVPreviewPanel = ({
  employee = null,
  previewHTML = '',
  previewData = null,
  qualityInfo = null,
  isLoading = false,
  isGenerating = false,
  isDownloadingDocx = false,
  isRegenerating = false,
  selectedCount = 0,
  onDownload,
  onDownloadDocx,
  onRegenerate,
  onGenerateSelected,
  templateName = '',
}) => {
  // No employee selected - show empty state
  if (!employee) {
    return (
      <div className="flex flex-col h-full bg-white">
        <EmptyState selectedCount={selectedCount} />
        
        {/* Generate Selected Button (bottom) */}
        {selectedCount > 0 && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onGenerateSelected}
              disabled={isGenerating}
              className="w-full btn btn-admin-primary"
            >
              {isGenerating ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Generate {selectedCount} CV{selectedCount !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-red-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {employee.full_name}
              </h2>
              <p className="text-sm text-gray-500">
                {employee.designation || 'No designation'} • {employee.staff_id}
              </p>
            </div>
          </div>
          
          {/* Action Buttons - Inline with employee name */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Download PDF Button */}
            <button
              onClick={() => onDownload?.(employee.staff_id)}
              disabled={isGenerating || !previewHTML}
              className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download PDF"
            >
              {isGenerating ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Download className="w-4 h-4" />
              )}
            </button>

            {/* Download DOCX Button */}
            <button
              onClick={() => onDownloadDocx?.(employee.staff_id)}
              disabled={isDownloadingDocx || !previewHTML}
              className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download DOCX"
            >
              {isDownloadingDocx ? (
                <LoadingSpinner size="sm" />
              ) : (
                <FileDown className="w-4 h-4" />
              )}
            </button>

            {/* Regenerate Button */}
            <button
              onClick={() => onRegenerate?.(employee.staff_id)}
              disabled={isRegenerating || isGenerating}
              className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Regenerate Preview"
            >
              <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Quality Score */}
        {qualityInfo && (
          <div className="mb-3">
            <QualityScoreBadge score={qualityInfo.quality_score || 0} />
          </div>
        )}

        {/* Missing Fields Alert */}
        {qualityInfo?.missing_fields?.length > 0 && (
          <div className="mt-3">
            <MissingFieldsAlert missingFields={qualityInfo.missing_fields} />
          </div>
        )}

        {/* Template Info */}
        {templateName && (
          <p className="mt-3 text-xs text-gray-500">
            Template: <span className="font-medium">{templateName}</span>
          </p>
        )}
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-3 text-sm text-gray-500">Loading preview...</p>
            </div>
          </div>
        ) : previewHTML ? (
          <div className="h-full overflow-auto p-4">
            <div 
              className="cv-preview prose max-w-none bg-white shadow-sm border border-gray-200 rounded-lg p-6"
              dangerouslySetInnerHTML={{ __html: previewHTML }}
            />
          </div>
        ) : previewData ? (
          <div className="h-full overflow-auto p-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">CV Data Preview</h3>
              <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(previewData, null, 2)}
              </pre>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No preview available</p>
              <p className="text-sm">Select a template to generate preview</p>
            </div>
          </div>
        )}
      </div>

      {/* Batch Generate Footer (if multiple selected) */}
      {selectedCount > 1 && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onGenerateSelected}
            disabled={isGenerating}
            className="w-full btn btn-admin-outline"
          >
            <FileText className="w-4 h-4 mr-2" />
            Generate All {selectedCount} Selected CVs
          </button>
        </div>
      )}
    </div>
  );
};

export default CVPreviewPanel;

